import { Suspense, lazy } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ConceptSearchCommand } from "@/components/ConceptSearchCommand";
import { ConceptSearchProvider } from "@/contexts/ConceptSearchContext";
import { routes } from "@/lib/routes";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { SentryErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const Problem = lazy(() => import("./pages/Problem"));
const MindMap = lazy(() => import("./pages/MindMap"));
const ConceptDetail = lazy(() => import("./pages/ConceptDetail"));
const Problems = lazy(() => import("./pages/Problems"));
const DefinitionQuiz = lazy(() => import("./pages/DefinitionQuiz"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const About = lazy(() => import("./pages/About"));
const Submissions = lazy(() => import("./pages/Submissions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="min-h-screen animate-fade-in">
      <Routes>
        <Route path="/" element={<Navigate to={routes.home} replace />} />
        <Route path={routes.login} element={<Auth />} />
        <Route path={routes.resetPassword} element={<ResetPassword />} />
        <Route path={routes.updatePassword} element={<UpdatePassword />} />
        <Route path={routes.home} element={<Index />} />
        <Route path="/problems/:problemId" element={<Problem />} />
        <Route path="/problems/:problemId/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
        <Route path={routes.mindMap} element={<MindMap />} />
        <Route path="/map/:conceptSlug" element={<ConceptDetail />} />
        <Route path={routes.problems} element={<ProtectedRoute><Problems /></ProtectedRoute>} />
        <Route path={routes.definitionQuiz} element={<ProtectedRoute><DefinitionQuiz /></ProtectedRoute>} />
        <Route path={routes.profile} element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path={routes.leaderboard} element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
        <Route path={routes.about} element={<About />} />
        <Route path={routes.privacyPolicy} element={<PrivacyPolicy />} />
        <Route path={routes.termsOfService} element={<TermsOfService />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

const LoadingFallback = () => (
  <div
    className="flex min-h-screen items-center justify-center bg-background animate-fade-in"
    aria-live="polite"
    aria-busy="true"
    role="status"
  >
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" aria-hidden="true" />
    <span className="sr-only">Loading…</span>
  </div>
);

const App = () => (
  <SentryErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Analytics />
          <BrowserRouter>
            <ConceptSearchProvider>
            <ConceptSearchCommand />
            <Suspense fallback={<LoadingFallback />}>
              <AnimatedRoutes />
              </Suspense>
            </ConceptSearchProvider>
          </BrowserRouter>
          <SpeedInsights />
        </TooltipProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
